// Who's In? — wolfwalker-api /api/whosin/* (minto_db whosin schema)
import { authService } from '../auth/authService';
import { addDaysISO, resolveForecastRange } from '../utils/whosIn/forecastRange';
import { buildFullRoster } from '../utils/whosIn/fullRoster';
import { enrichRosterWithNextMovement } from '../utils/whosIn/nextMovement';

export { addDaysISO };

class WhosInService {
  constructor() {
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    this.basePath = '/api/whosin';
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken('qgisApi');
      const userInfo = authService.getUserInfo();
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  _buildUrl(segments = [], params = {}) {
    const path = segments.length ? `${this.basePath}/${segments.join('/')}` : `${this.basePath}`;
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v) !== '') {
        qs.append(k, String(v));
      }
    });
    const q = qs.toString();
    return `${this.functionAppUrl}${path}${q ? `?${q}` : ''}`;
  }

  async _get(segments = [], params = {}, options = {}) {
    const { timeoutMs = 30000 } = options;
    const headers = await this.getAuthenticatedHeaders();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(this._buildUrl(segments, params), {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const text = await response.text();
      let body;
      try {
        body = text ? JSON.parse(text) : {};
      } catch {
        body = { raw: text };
      }
      if (!response.ok) {
        console.error(
          'WhosIn API error:',
          response.status,
          body?.error || body?.message || body,
        );
        throw new Error(
          body?.message || body?.error || `Who's In request failed: ${response.statusText}`,
        );
      }
      if (body.success === false && body.error) {
        throw new Error(body.message || body.error || 'Who\'s In request failed');
      }
      return body;
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  }

  /**
   * Employee directory filters: department/team/cost_code exact match, search name ILIKE, is_active optional
   */
  async getEmployees(options = {}) {
    const {
      limit = 500,
      offset = 0,
      department,
      team,
      cost_code,
      search,
      q,
      is_active,
    } = options;
    return this._get(['employees'], {
      limit,
      offset,
      department,
      team,
      cost_code,
      search: search || q,
      is_active:
        typeof is_active === 'boolean'
          ? is_active
            ? 'true'
            : 'false'
          : undefined,
    });
  }

  /** Single employee profile */
  async getEmployee(id) {
    const body = await this._get(['employees', String(id)], {});
    return body?.data ?? body;
  }

  /** Timeline for one person (requires from/to dates YYYY-MM-DD) */
  async getEmployeeSchedule(employeeId, from, to) {
    const body = await this._get(['employees', String(employeeId), 'schedule'], { from, to });
    return body?.data ?? body;
  }

  /** Everyone scheduled on a calendar day (+ optional snapshot from daily_summary) */
  async getScheduleDaily(date, options = {}) {
    const { preferSummary = false } = options;
    const body = await this._get(['schedule', 'daily'], {
      date,
      prefer_summary: preferSummary ? 'true' : 'false',
    });
    return body?.data ?? body;
  }

  /** Flat join rows across a bounded date range */
  async getScheduleRange(from, to) {
    const body = await this._get(['schedule', 'range'], { from, to });
    return body?.data ?? body;
  }

  /** Series of headcount buckets by day computed from daily_schedule */
  async getHeadcountDaily(from, to) {
    const body = await this._get(['headcount', 'daily'], { from, to });
    return body?.data ?? body;
  }

  /** Pre-aggregated daily_summary merged with computed fallbacks — either date or from/to */
  async getHeadcountSummary(options = {}) {
    const { date, from, to } = options;
    const params = {};
    if (date) params.date = date;
    else {
      params.from = from;
      params.to = to;
    }
    const body = await this._get(['headcount', 'summary'], params);
    return body;
  }

  /** Inbound/outbound counts (and optionally names) by day */
  async getInboundOutbound(from, to, options = {}) {
    const { includeNames = false } = options;
    const body = await this._get(['logistics', 'inbound-outbound'], {
      from,
      to,
      include_names: includeNames ? 'true' : 'false',
    });
    return body?.data ?? body;
  }

  async getImportsLatest() {
    const body = await this._get(['imports', 'latest'], {});
    return body?.data ?? body;
  }

  async getImports(options = {}) {
    const { limit = 50, offset = 0 } = options;
    return this._get(['imports'], { limit, offset });
  }

  /** Home page dashboard bundle (imports + summaries + roster + extended forecast + logistics). */
  async getDashboardPayload(options = {}) {
    const { date } = options;
    if (!date) {
      throw new Error('date (YYYY-MM-DD) is required');
    }

    const latestRes = await this.getImportsLatest().catch(() => ({ latest: null }));
    const imports = latestRes?.latest ?? null;
    const forecastRange = resolveForecastRange(date, imports);

    const [summaryRes, dailyRes, forecastRes, logisticsRes, scheduleRangeRes, employeesRes] =
      await Promise.all([
        this.getHeadcountSummary({ date }),
        this.getScheduleDaily(date, { preferSummary: true }),
        this.getHeadcountDaily(forecastRange.from, forecastRange.to).catch(() => ({ daily: [] })),
        this.getInboundOutbound(forecastRange.from, forecastRange.to, { includeNames: true }).catch(
          () => ({ daily: [] }),
        ),
        this.getScheduleRange(date, forecastRange.to).catch(() => ({ schedule_rows: [] })),
        this.getEmployees({ is_active: true, limit: 5000 }).catch(() => ({ data: { employees: [] } })),
      ]);

    const roster = dailyRes?.schedule_rows ?? [];
    const onSiteRoster = roster.filter((row) => row.is_on_site === true);
    const scheduleRangeRows = scheduleRangeRes?.schedule_rows ?? [];
    const activeEmployees = employeesRes?.data?.employees ?? employeesRes?.employees ?? [];
    const fullRoster = buildFullRoster(activeEmployees, roster, scheduleRangeRows, date);
    const onSiteRosterEnriched = enrichRosterWithNextMovement(
      onSiteRoster,
      scheduleRangeRows,
      date,
    );

    const logisticsDaily = logisticsRes?.daily ?? [];
    const logisticsByDate = {};
    logisticsDaily.forEach((row) => {
      const key =
        typeof row.schedule_date === 'string'
          ? row.schedule_date.slice(0, 10)
          : String(row.schedule_date);
      logisticsByDate[key] = row;
    });

    return {
      success: true,
      date,
      imports,
      summaries: summaryRes?.data?.summaries ?? [],
      summaryMeta: summaryRes?.meta ?? {},
      roster,
      fullRoster,
      onSiteRoster,
      onSiteRosterEnriched,
      summarySnapshot: dailyRes?.summary_snapshot ?? null,
      logisticsDaily: logisticsByDate[date] ? [logisticsByDate[date]] : [],
      logisticsByDate,
      forecast: forecastRes?.daily ?? [],
      forecastRange,
    };
  }

  /** Inbound/outbound names for a single day (on-demand). */
  async getDayLogistics(date) {
    const data = await this.getInboundOutbound(date, date, { includeNames: true });
    const row = data?.daily?.[0];
    return row ?? null;
  }

  /** Full roster + logistics for forecast day modal. */
  async getDayDetail(date) {
    const [logisticsRes, dailyRes] = await Promise.all([
      this.getInboundOutbound(date, date, { includeNames: true }).catch(() => ({ daily: [] })),
      this.getScheduleDaily(date).catch(() => ({ schedule_rows: [] })),
    ]);
    const logistics = logisticsRes?.daily?.[0] ?? null;
    const roster = dailyRes?.schedule_rows ?? [];
    return {
      logistics,
      roster,
      onSiteRoster: roster.filter((row) => row.is_on_site === true),
      summarySnapshot: dailyRes?.summary_snapshot ?? null,
    };
  }

  filterData(data, searchTerm) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter((item) => JSON.stringify(item).toLowerCase().includes(term));
  }

  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      const aVal = a[field] ?? '';
      const bVal = b[field] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (direction === 'asc') {
        return String(aVal) > String(bVal) ? 1 : -1;
      }
      return String(aVal) < String(bVal) ? 1 : -1;
    });
  }
}

export const whosInService = new WhosInService();
export default whosInService;
